import http from "@/apps/shared/baseService/config";

const base = {
  rota: "clientes",
  nome: "Clientes"
};

const CLIENTS_CACHE_KEY = "clientes";
const CLIENT_CACHE_FIELDS = [
  "_id",
  "id_cliente",
  "client_type",
  "nome",
  "razao_social",
  "email",
  "cpf",
  "cnpj",
  "telefone",
  "whatsapp_links",
  "endereco",
  "data_nascimento",
  "sexo",
  "cidade",
  "estado",
  "obs",
  "photo",
  "imagens",
  "created_at",
  "from_tenant"
];

const readClientsCache = () => {
  try {
    const cached = localStorage.getItem(CLIENTS_CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    return parsed && Array.isArray(parsed.docs) ? parsed : null;
  } catch (error) {
    try {
      localStorage.removeItem(CLIENTS_CACHE_KEY);
    } catch (storageError) {
      // A requisicao ao backend continua mesmo sem acesso ao storage.
    }
    return null;
  }
};

const compactClient = client => CLIENT_CACHE_FIELDS.reduce((cached, field) => {
  if (client[field] !== undefined && client[field] !== null) {
    cached[field] = client[field];
  }
  return cached;
}, {});

const persistClientsCache = payload => {
  if (!payload || !Array.isArray(payload.docs)) return;

  const cachedPayload = {
    ...payload,
    docs: payload.docs.map(compactClient),
    cache_version: 2,
    cached_at: new Date().toISOString()
  };
  const serialized = JSON.stringify(cachedPayload);

  try {
    localStorage.setItem(CLIENTS_CACHE_KEY, serialized);
  } catch (error) {
    try {
      localStorage.removeItem(CLIENTS_CACHE_KEY);
      localStorage.setItem(CLIENTS_CACHE_KEY, serialized);
    } catch (storageError) {
      // O Vuex ja recebeu a lista; falha de cache nao pode interromper a tela.
    }
  }
};

const scheduleClientsCache = payload => {
  setTimeout(() => persistClientsCache(payload), 0);
};

export const service = {
  criar: data => http.post(`/admin/${base.rota}/create`, data),
  listar: data => http.post(`/admin/${base.rota}/list`, data),
  atualizar: data => http.post(`/admin/${base.rota}/update`, data),
  excluir: data => http.post(`/admin/${base.rota}/delete`, data),
  excluir_muitos: data => http.post(`/admin/${base.rota}/delete-many`, data)
};

const module = {
  actions: {
    listar_clientes: ({ commit, dispatch, getters }) => {
      const cached = readClientsCache();
      if (cached) {
        commit("set_clientes", cached);
      } else {
        dispatch("enableLoading");
      }

      return service
        .listar(getters.get_clientes_filtros)
        .then(res => {
          dispatch("disableLoading");
          if (res.data) {
            commit("set_clientes", res.data);
            scheduleClientsCache(res.data);
          }
          return res.data;
        })
        .catch(e => {
          dispatch("disableLoading");
          dispatch("createGlobalMessage", {
            type: "error",
            message: e.response && e.response.data ? e.response.data.message : e.message,
            timeout: 6000
          });
          return null;
        });
    },
    atualizar_cliente: ({ commit, dispatch, getters }) => {
      dispatch("enableLoading");
      return service
        .atualizar({ ...getters.get_cliente, imagens: getters.getLinks })
        .then(res => {
          dispatch("disableLoading");
          commit("set_cliente", res.data);
          dispatch("createGlobalMessage", {
            type: "success",
            message: "Atualização Concluída!",
            timeout: 6000
          });
          dispatch("listar_clientes");
          return res.data;
        })
        .catch(e => {
          dispatch("disableLoading");
          dispatch("createGlobalMessage", {
            type: "error",
            message: e.response && e.response.data ? e.response.data.message : e.message,
            timeout: 6000
          });
          return null;
        });
    },
    criar_cliente: ({ commit, dispatch, getters }, save) => {
      dispatch("enableLoading");
      return service
        .criar({ ...getters.get_cliente, imagens: getters.getLinks })
        .then((res) => {
          dispatch("disableLoading");
          if (save) {
            commit("set_compra", {
              ...getters.get_compra,
              cliente: res.data
            });
          }
          commit("set_cliente", res.data);
          commit("setLinks", []);
          dispatch("createGlobalMessage", {
            type: "success",
            message: "Registro Concluído!",
            timeout: 6000
          });
          dispatch("listar_clientes");
          commit("set_modal_view_cliente", false);
          return res.data;
        })
        .catch(e => {
          dispatch("disableLoading");
          dispatch("createGlobalMessage", {
            type: "error",
            message: e.response && e.response.data ? e.response.data.message : e.message,
            timeout: 6000
          });
          return null;
        });
    },
    excluir_cliente: ({ dispatch }, payload) => {
      dispatch("enableLoading");
      service
        .excluir(payload)
        .then(() => {
          dispatch("disableLoading");
          dispatch("createGlobalMessage", {
            type: "success",
            message: "Exclusão Concluída!",
            timeout: 6000
          });
          dispatch("listar_clientes");
        })
        .catch(e => {
          dispatch("disableLoading");
          dispatch("createGlobalMessage", {
            type: "error",
            message: e.response.data.message,
            timeout: 6000
          });
        });
    },
    abre_modal_view_cliente: ({ commit }, payload) => {
      const hasClientPayload = payload && typeof payload === "object" && (payload._id || payload.id);
      if (hasClientPayload) {
        commit("set_cliente", {
          ...payload,
          whatsapp_links: Array.isArray(payload.whatsapp_links) ? [...payload.whatsapp_links] : []
        });
        commit("setLinks", Array.isArray(payload.imagens) ? payload.imagens : []);
      } else {
        commit("set_cliente", {
          new: true,
          client_type: "pf",
          whatsapp_links: []
        });
        commit("setLinks", []);
      }
      commit("set_modal_view_cliente", true);
    },
    fecha_modal_view_cliente: ({ commit }) => {
      commit("set_cliente", {});
      commit("setLinks", []);
      commit("set_modal_view_cliente", false);
    }
  },
  state: {
    cliente: {},
    clientes: {
      docs: [],
      page: 1,
      totalPages: 1
    },
    clientes_filtros: {
      limitPerPage: 10,
      page: 1,
      dataInicio: "",
      dataFim: ""
    },
    modal_view_cliente: false
  },
  mutations: {
    set_cliente: (state, payload) => (state.cliente = payload),
    set_clientes: (state, payload) => (state.clientes = payload),
    set_modal_view_cliente: (state, payload) =>
      (state.modal_view_cliente = payload)
  },
  getters: {
    get_cliente: state => state.cliente,
    get_clientes: state => state.clientes,
    get_modal_view_cliente: state => state.modal_view_cliente,
    get_clientes_filtros: state => state.clientes_filtros
  }
};

const routes = [
  {
    path: `/${base.rota}`,
    component: () => import("./views/Main.vue"),
    name: base.nome
  }
];

export default {
  service,
  module,
  routes
};
